"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { DismissableLayerBranch } from "@radix-ui/react-dismissable-layer";
import { X } from "lucide-react";
import * as React from "react";
import {
  DialogHostContext,
  NESTED_IN_HOST_SURFACE_CLASS,
  useOverlayHostAtOpen,
} from "../overlay-host";
import { OverlayPlacementProvider } from "../overlay-placement";
import {
  OverlayPortalContext,
  popDefaultOverlayPortal,
  pushDefaultOverlayPortal,
  registerNestedSurfaceHost,
  unregisterNestedSurfaceHost,
} from "../overlay-portal";
import { cn } from "../utils";
import { DialogFooter, partitionDialogChildren } from "./dialog-content-slots";
import { composeDialogDismissGuard } from "./dialog-portaled-overlay-guard";

interface DialogProps extends DialogPrimitive.DialogProps {
  /**
   * This dialog carries its own `z-[1xx]` on content and overlay and stacks
   * on `document.body` above host popups (file preview `z-[105]`, DMS picker
   * `z-[140]`, the task popup `z-[100]` itself). Gate:
   * `scripts/check-dialog-above-hosts.mjs`.
   *
   * Without it, a dialog that opens while a host branch is registered on the
   * overlay-portal stack (`popoverPortal` / `SuggestionDialogContainerProvider`)
   * mounts inside that branch, non-modal, at `z-[110]`. On the body it would
   * sit behind the host at `z-50` — invisible — while the host hides its own
   * close chrome because a "stacked" dialog is open (16.09.2026: „Vorgang
   * drucken" inside the Stammdaten task popup left the popup unclosable).
   */
  readonly aboveHosts?: boolean;
}

/** Open state of the enclosing `Dialog`; content outside one counts as open. */
const DialogOpenContext: React.Context<boolean> =
  React.createContext<boolean>(true);

/**
 * Radix root plus the host decision: read the overlay-portal stack once when
 * the dialog opens, hand the branch to `DialogContent` and drop the second
 * modal while nested (a nested modal leaves `pointer-events: none` on the
 * host layer after close). Explicit `modal` wins.
 */
function Dialog({
  open,
  defaultOpen,
  onOpenChange,
  modal,
  aboveHosts = false,
  children,
}: DialogProps): React.ReactElement {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState<boolean>(
    defaultOpen ?? false,
  );
  const isOpen: boolean = open ?? uncontrolledOpen;
  const hostAtOpen: HTMLElement | null = useOverlayHostAtOpen(isOpen);
  const host: HTMLElement | null = aboveHosts ? null : hostAtOpen;
  const handleOpenChange = React.useCallback(
    (next: boolean): void => {
      setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );
  return (
    <DialogHostContext.Provider value={host}>
      <DialogOpenContext.Provider value={isOpen}>
        <DialogPrimitive.Root
          open={open}
          defaultOpen={defaultOpen}
          onOpenChange={handleOpenChange}
          modal={modal ?? host == null}
        >
          {children}
        </DialogPrimitive.Root>
      </DialogOpenContext.Provider>
    </DialogHostContext.Provider>
  );
}

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    // Marker für die zentrale Dismiss-Logik (`has-open-radix-dialogs.ts`,
    // `dialog-portaled-overlay-guard.ts`). Radix selbst rendert KEIN
    // `data-radix-*` auf Overlay und Content — nur `data-state` und beim
    // Content `role="dialog"`. Ohne diese beiden Marker traf jeder Selektor
    // ins Leere und sämtliche Escape-/Outside-Click-Weichen waren tot.
    data-radix-dialog-overlay=""
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * Full-viewport / inset shells must pass this (or `disableViewportLock`) so
 * the default max-height does not letterbox them.
 */
const DIALOG_CONTENT_FULLSCREEN_CLASS_NAME = "max-h-none";

const DIALOG_BODY_BASE_CLASS_NAME =
  "flex min-h-0 min-w-0 grow flex-col gap-[inherit] [&>*]:min-w-0";

const DIALOG_BODY_SCROLL_CLASS_NAME = `${DIALOG_BODY_BASE_CLASS_NAME} overflow-y-auto overscroll-contain`;

const DIALOG_BODY_UNLOCKED_CLASS_NAME = `${DIALOG_BODY_BASE_CLASS_NAME} overflow-visible`;

/**
 * Default dialogs cap to the viewport and scroll inside the body slot.
 * Fullscreen / `overflow-visible` shells opt out via prop or className.
 */
function dialogLocksToViewport(
  disableViewportLock: boolean | undefined,
  className: string | undefined,
): boolean {
  if (disableViewportLock === true) return false;
  if (typeof className !== "string") return true;
  if (/\boverflow-visible\b/.test(className)) return false;
  if (/\bmax-h-none\b/.test(className)) return false;
  return true;
}

/**
 * Close-X room, reserved on the header row only. Old first-child rule padded whole forms — empty right column.
 */
const DialogCloseSafeAreaContext: React.Context<boolean> =
  React.createContext<boolean>(false);

const DIALOG_CLOSE_SAFE_AREA_CLASS = "pr-12";

/**
 * Close-Button-Styling. `bg-background` ist bewusst deckend (nicht nur on-hover):
 * so maskiert der Button Inhalt, der trotz Safe-Area darunter geriete (z.B. bei
 * Custom-Headern) — es entsteht nie ein Text-auf-Icon-Overlap. Auf Standard-
 * Dialogen (heller Content-Bereich) ist der Hintergrund unsichtbar.
 */
const DIALOG_CLOSE_BUTTON_CLASS =
  "absolute right-3 top-3 z-50 inline-flex h-7 w-7 items-center justify-center rounded-md bg-background text-muted-foreground/70 ring-offset-background transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground";

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideCloseButton?: boolean;
    hideOverlay?: boolean;
    /** Merged into the default overlay (e.g. raise z-index above local stacking contexts). */
    overlayClassName?: string;
    /** Skip auto `pr-12` on DialogHeader / bare DialogTitle; caller reserves X room itself. */
    disableCloseSafeArea?: boolean;
    /**
     * Skip viewport max-height and the inner scroll body. Also inferred from
     * `max-h-none` or `overflow-visible` on `className` (fullscreen / compose).
     */
    disableViewportLock?: boolean;
    /**
     * Set this on every dialog that contains a picker with a search field.
     *
     * Without it a `PopoverContent` portals to `document.body` — outside this
     * element. The modal dialog's FocusScope is `trapped` and pulls any focus
     * that leaves its container straight back to the trigger, so the picker's
     * list shows but its input takes neither click nor cursor. Measured in the
     * browser: `input.focus()` fails while the popover hangs on `<body>` and
     * works the moment it sits inside the dialog.
     *
     * Turning it on also rewrites two things the branch depends on: centering
     * moves off `translate-*` (a transform makes this element the containing
     * block for `position: fixed`, so the popover would be placed against the
     * dialog instead of the viewport) and `overflow` becomes visible (hidden
     * would clip the popover). Dialogs that need clipped corners put their own
     * rounded, overflow-hidden frame inside.
     */
    popoverPortal?: boolean;
    /**
     * Where this dialog's portal mounts. Explicit override; without it the
     * branch `Dialog` resolved when it opened is used (the innermost host on
     * the overlay-portal stack), else `document.body`.
     *
     * Same trap as `popoverPortal`, one level up: on `document.body` the
     * portal lands outside the open host dialog. The host holds
     * `pointer-events: none` on the body, a trapped FocusScope and a higher
     * `z-index`, so the nested dialog renders but is neither seen nor
     * clickable — it looks like the button did nothing at all, and the host
     * hides its close chrome for the "stacked" dialog on top.
     *
     * Callers that style the nested surface themselves pass the container
     * from `useNestedDialogInModalMount(open)` (app) and set `modal` on the
     * root; everything else gets the same treatment automatically.
     */
    container?: HTMLElement | null;
  }
>(
  (
    {
      className,
      children,
      hideCloseButton,
      hideOverlay,
      overlayClassName,
      disableCloseSafeArea,
      disableViewportLock,
      popoverPortal,
      container,
      onPointerDownOutside,
      onInteractOutside,
      onFocusOutside,
      ...props
    },
    ref,
  ) => {
    const lockToViewport: boolean =
      popoverPortal === true
        ? false
        : dialogLocksToViewport(disableViewportLock, className);
    const { bodyChildren, footerChildren } = partitionDialogChildren(children);

    // Host branch resolved by `Dialog` when this dialog opened; an explicit
    // `container` wins (callers of `useNestedDialogInModalMount` style the
    // nested surface themselves).
    const host: HTMLElement | null = React.useContext(DialogHostContext);
    const nestedInHost: boolean = container == null && host != null;
    const portalContainer: HTMLElement | undefined =
      container ?? host ?? undefined;

    const [branchEl, setBranchEl] = React.useState<HTMLDivElement | null>(null);
    const [nestedHostEl, setNestedHostEl] =
      React.useState<HTMLDivElement | null>(null);
    // Hosts only while open: a surface opening during the exit animation would mount here and vanish.
    const dialogOpen: boolean = React.useContext(DialogOpenContext);
    const hostsPickers: boolean = popoverPortal === true && dialogOpen;
    React.useLayoutEffect(() => {
      if (!hostsPickers || branchEl == null || nestedHostEl == null) return;
      // Dialogs and sheets opened over this one go beside the branch.
      registerNestedSurfaceHost(branchEl, nestedHostEl);
      pushDefaultOverlayPortal(branchEl);
      return () => {
        popDefaultOverlayPortal(branchEl);
        unregisterNestedSurfaceHost(branchEl);
      };
    }, [hostsPickers, branchEl, nestedHostEl]);
    const outerPickerBranch: HTMLElement | null =
      React.useContext(OverlayPortalContext);
    const pickerBranch: HTMLElement | null =
      (hostsPickers ? branchEl : null) ?? outerPickerBranch;
    return (
      <DialogPortal container={portalContainer}>
        {!hideOverlay ? (
          <DialogOverlay
            className={cn(
              nestedInHost && NESTED_IN_HOST_SURFACE_CLASS,
              overlayClassName,
            )}
          />
        ) : null}
        <DialogPrimitive.Content
          ref={ref}
          // Siehe DialogOverlay: Radix rendert diesen Marker nicht selbst.
          data-radix-dialog-content=""
          onPointerDownOutside={composeDialogDismissGuard(onPointerDownOutside)}
          onInteractOutside={composeDialogDismissGuard(onInteractOutside)}
          onFocusOutside={composeDialogDismissGuard(onFocusOutside)}
          className={cn(
            // `w-[calc(100%-2rem)]` (instead of `w-full`) guarantees a 1rem side
            // margin on phones so dialogs never sit edge-to-edge with clipped
            // corners. On desktop `max-w-*` caps it long before that matters.
            // Consumers that need full-bleed override `w-*` explicitly.
            // Locked: outer clips, inner body scrolls, footer + close stay pinned.
            // Unlocked (`max-h-none` / `overflow-visible` / `disableViewportLock`):
            // shell owns height and overflow.
            "fixed left-[50%] top-[50%] z-50 flex w-[calc(100%-2rem)] min-w-0 max-w-lg translate-x-[-50%] translate-y-[-50%] flex-col gap-4 overflow-hidden rounded-lg border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] data-[state=closed]:pointer-events-none",
            popoverPortal === true &&
              "!left-0 !top-0 !right-0 !bottom-0 m-auto h-fit !translate-x-0 !translate-y-0 overflow-visible",
            lockToViewport && "max-h-[calc(100dvh-2rem)]",
            className,
            nestedInHost && NESTED_IN_HOST_SURFACE_CLASS,
            lockToViewport && "overflow-hidden",
          )}
          {...props}
          style={
            popoverPortal === true
              ? { transform: "none", overflow: "visible", ...props.style }
              : props.style
          }
        >
          {popoverPortal === true ? (
            <>
              <DismissableLayerBranch
                ref={setBranchEl}
                data-dialog-picker-branch=""
                className="pointer-events-none fixed inset-0 z-[100] overflow-visible"
              />
              {/* Not a branch: clicks inside a nested dialog must still reach its own popovers. */}
              <div
                ref={setNestedHostEl}
                data-dialog-nested-host=""
                className="pointer-events-none fixed inset-0 z-[100] overflow-visible"
              />
            </>
          ) : null}
          {/* A dialog is a new surface: region placements of the page that
              opened it (task details column) do not reach its popovers. */}
          <OverlayPortalContext.Provider value={pickerBranch}>
            <OverlayPlacementProvider placement={null}>
              <DialogCloseSafeAreaContext.Provider
                value={!hideCloseButton && !disableCloseSafeArea}
              >
                <div
                  data-slot="dialog-body"
                  className={
                    lockToViewport
                      ? DIALOG_BODY_SCROLL_CLASS_NAME
                      : DIALOG_BODY_UNLOCKED_CLASS_NAME
                  }
                >
                  {bodyChildren}
                </div>
                {footerChildren}
              </DialogCloseSafeAreaContext.Provider>
            </OverlayPlacementProvider>
          </OverlayPortalContext.Provider>
          {!hideCloseButton && (
            <DialogPrimitive.Close className={DIALOG_CLOSE_BUTTON_CLASS}>
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const reserveCloseSpace: boolean = React.useContext(
    DialogCloseSafeAreaContext,
  );
  return (
    // Header owns the X room; a title inside must not reserve it twice.
    <DialogCloseSafeAreaContext.Provider value={false}>
      <div
        className={cn(
          "flex flex-col space-y-1.5 text-center sm:text-left",
          className,
          // Last: caller px-* must not merge away the X room.
          reserveCloseSpace && DIALOG_CLOSE_SAFE_AREA_CLASS,
        )}
        {...props}
      />
    </DialogCloseSafeAreaContext.Provider>
  );
};
DialogHeader.displayName = "DialogHeader";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => {
  // Title without DialogHeader reserves the X room itself.
  const reserveCloseSpace: boolean = React.useContext(
    DialogCloseSafeAreaContext,
  );
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn(
        "text-lg font-semibold leading-none tracking-tight",
        className,
        reserveCloseSpace && DIALOG_CLOSE_SAFE_AREA_CLASS,
      )}
      {...props}
    />
  );
});
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DIALOG_CONTENT_FULLSCREEN_CLASS_NAME,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  // For a raw `DialogPrimitive.Content` (custom fullscreen chrome): the same
  // dismiss guard `DialogContent` composes itself.
  composeDialogDismissGuard,
};
